/**
 * Lexa — Assistante IA conversationnelle Lexback.
 *
 * Sprint 1 frontend (v2) :
 * - Conversation textuelle avec Lexa via /api/lexa/chat
 * - Barre "Actions rapides" TOUJOURS visible au-dessus du composer
 *   · Récupérer statuts / RNE / avis INSEE d'une société (modale SIREN → Pappers)
 *   · Ajouter un dossier (redirige vers /dossiers/nouveau)
 *   · Possibilité de créer/supprimer ses propres raccourcis (localStorage)
 * - Sidebar conversations récentes + bouton "Nouvelle conversation"
 * - Indicateur de réflexion + tool calls visibles (transparence)
 *
 * Design : espresso #1f1612 + or #d9a847, fonts Syne + DM Sans.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('lexback_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ── Design tokens ──
const C = {
  bg: '#fafaf8',
  surface: '#ffffff',
  border: 'rgba(0,0,0,0.08)',
  borderSoft: 'rgba(0,0,0,0.04)',
  text: '#1d1d1f',
  textMuted: '#5C6B7A',
  textSubtle: '#8b8576',
  gold: 'var(--brand-primary, #d9a847)',
  goldSoft: 'rgba(217,168,71,0.08)',
  goldBorder: 'rgba(217,168,71,0.25)',
  espresso: '#1f1612',
  userBubble: '#f3f3f0',
  assistantBubble: '#fffdf9',
  toolBg: 'rgba(217,168,71,0.06)',
};

// ── Actions rapides par défaut ──
const DEFAULT_QUICK_ACTIONS = [
  { id: 'statuts',  label: 'Récupérer les statuts',      type: 'pappers', docKind: 'statuts',     icon: '📄' },
  { id: 'rne',      label: 'Récupérer le RNE',           type: 'pappers', docKind: 'extrait',     icon: '📋' },
  { id: 'insee',    label: 'Récupérer l\'avis INSEE',    type: 'pappers', docKind: 'avis_insee',  icon: '🏛' },
  { id: 'nouveau',  label: 'Ajouter un dossier',         type: 'add_dossier',                          icon: '➕' },
];

const CUSTOM_SHORTCUTS_KEY = 'lexback_lexa_custom_shortcuts';

export default function Lexa() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Raccourcis personnalisés (localStorage)
  const [customShortcuts, setCustomShortcuts] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_SHORTCUTS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // Modales
  const [pappersModal, setPappersModal] = useState(null);
  const [addShortcutModal, setAddShortcutModal] = useState(false);
  const [addDossierChoiceModal, setAddDossierChoiceModal] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(CUSTOM_SHORTCUTS_KEY, JSON.stringify(customShortcuts)); }
    catch (e) { console.error('Persist shortcuts failed', e); }
  }, [customShortcuts]);

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const res = await api.get('/api/lexa/conversations?limit=20');
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur chargement conversations', e);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadConversation = useCallback(async (convId) => {
    if (!convId) {
      setMessages([]);
      setCurrentConvId(null);
      return;
    }
    setMessagesLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/lexa/conversations/${convId}`);
      setMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
      setCurrentConvId(convId);
    } catch (e) {
      console.error('Erreur chargement conversation', e);
      setError('Impossible de charger cette conversation');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const sendMessage = async (overrideText) => {
    const text = (overrideText !== undefined ? overrideText : draft).trim();
    if (!text || sending) return;

    setError('');
    setSending(true);

    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    if (overrideText === undefined) setDraft('');

    try {
      const res = await api.post('/api/lexa/chat', {
        conversation_id: currentConvId || undefined,
        message: text,
      });
      const data = res.data;

      if (!currentConvId && data.conversation_id) {
        setCurrentConvId(data.conversation_id);
      }

      const [msgRes] = await Promise.all([
        api.get(`/api/lexa/conversations/${data.conversation_id}`),
        loadConversations(),
      ]);
      setMessages(Array.isArray(msgRes.data.messages) ? msgRes.data.messages : []);
    } catch (e) {
      console.error('Erreur envoi message', e);
      const detail = e?.response?.data?.detail || 'Erreur lors de l\'envoi. Réessaie.';
      setError(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi');
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
      if (overrideText === undefined) setDraft(text);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const newConversation = () => {
    setCurrentConvId(null);
    setMessages([]);
    setError('');
    setDraft('');
    textareaRef.current?.focus();
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await api.delete(`/api/lexa/conversations/${convId}`);
      if (currentConvId === convId) newConversation();
      await loadConversations();
    } catch (err) {
      console.error('Erreur suppression', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (action) => {
    if (action.type === 'pappers') {
      setPappersModal({ docKind: action.docKind, label: action.label });
    } else if (action.type === 'add_dossier') {
      setAddDossierChoiceModal(true);
    } else if (action.type === 'navigate') {
      navigate(action.path);
    } else if (action.type === 'prompt') {
      sendMessage(action.prompt);
    }
  };

  const addCustomShortcut = (label, prompt) => {
    const cleanLabel = label.trim();
    const cleanPrompt = prompt.trim();
    if (!cleanLabel || !cleanPrompt) return;
    setCustomShortcuts((prev) => [
      ...prev,
      { id: 'custom-' + Date.now(), label: cleanLabel, prompt: cleanPrompt, type: 'prompt', icon: '✦' },
    ]);
    setAddShortcutModal(false);
  };

  const removeCustomShortcut = (id) => {
    setCustomShortcuts((prev) => prev.filter((s) => s.id !== id));
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const dt = new Date(iso);
    const diff = (Date.now() - dt.getTime()) / 1000;
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const allQuickActions = [...DEFAULT_QUICK_ACTIONS, ...customShortcuts];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '260px 1fr',
      gap: '16px',
      height: 'calc(100vh - 96px)',
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 1rem',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      color: C.text,
      letterSpacing: '-0.011em',
    }}>

      {/* ─── Sidebar conversations ─── */}
      <aside style={{
        background: C.surface,
        border: `0.5px solid ${C.border}`,
        borderRadius: '12px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <button
          onClick={newConversation}
          style={{
            background: C.espresso, color: '#fff', border: 'none',
            padding: '10px 14px', borderRadius: '8px',
            fontSize: '12.5px', fontWeight: 500, cursor: 'pointer',
            fontFamily: 'inherit', marginBottom: '14px',
            display: 'flex', alignItems: 'center', gap: '7px', justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: '14px' }}>+</span>
          <span>Nouvelle conversation</span>
        </button>

        <div style={{
          fontSize: '9.5px', color: C.textSubtle,
          textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600,
          marginBottom: '8px', paddingLeft: '4px',
        }}>
          Récentes
        </div>

        <div style={{ overflowY: 'auto', flex: 1, marginRight: '-4px', paddingRight: '4px' }}>
          {conversationsLoading ? (
            <div style={{ fontSize: '11.5px', color: C.textSubtle, padding: '8px' }}>Chargement…</div>
          ) : conversations.length === 0 ? (
            <div style={{ fontSize: '11.5px', color: C.textSubtle, padding: '8px', fontStyle: 'italic' }}>
              Aucune conversation pour l'instant.
            </div>
          ) : (
            conversations.map((c) => {
              const isActive = c.id === currentConvId;
              return (
                <div
                  key={c.id}
                  onClick={() => loadConversation(c.id)}
                  style={{
                    padding: '8px 10px', borderRadius: '7px', cursor: 'pointer',
                    background: isActive ? C.goldSoft : 'transparent',
                    border: isActive ? `0.5px solid ${C.goldBorder}` : '0.5px solid transparent',
                    marginBottom: '3px',
                    display: 'flex', alignItems: 'flex-start', gap: '6px',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#fafaf8'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '12px', fontWeight: 500, color: C.text,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {c.title || 'Sans titre'}
                    </div>
                    <div style={{
                      fontSize: '10px', color: C.textSubtle, marginTop: '2px',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmtDate(c.last_message_at)} · {c.message_count} msg
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteConversation(c.id, e)}
                    title="Supprimer"
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: '12px', color: C.textSubtle, opacity: 0.5,
                      padding: '0 4px', fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = '#b91c1c'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = C.textSubtle; }}
                  >×</button>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* ─── Zone de chat ─── */}
      <main style={{
        background: C.surface,
        border: `0.5px solid ${C.border}`,
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: `0.5px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: C.espresso, color: C.gold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'Syne', sans-serif", fontSize: '15px', fontWeight: 600,
          }}>L</div>
          <div>
            <div style={{
              fontFamily: "'Syne', sans-serif", fontSize: '15px', fontWeight: 600,
              color: C.text, lineHeight: 1.2,
            }}>
              Lexa
            </div>
            <div style={{ fontSize: '11px', color: C.textSubtle, marginTop: '1px' }}>
              Assistante Lexback
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '20px',
          display: 'flex', flexDirection: 'column', gap: '14px',
        }}>
          {messagesLoading ? (
            <div style={{ fontSize: '12px', color: C.textSubtle, textAlign: 'center', padding: '20px' }}>
              Chargement de la conversation…
            </div>
          ) : messages.length === 0 && !sending ? (
            <EmptyState />
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}

          {sending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '6px',
                background: C.espresso, color: C.gold,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Syne', sans-serif", fontSize: '11px', fontWeight: 600,
                flexShrink: 0,
              }}>L</div>
              <div style={{ display: 'flex', gap: '3px' }}>
                <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
              </div>
              <span style={{ fontSize: '11px', color: C.textSubtle, fontStyle: 'italic' }}>
                Lexa réfléchit…
              </span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Erreur */}
        {error && (
          <div style={{
            padding: '8px 20px',
            background: 'rgba(185,28,28,0.06)',
            borderTop: '0.5px solid rgba(185,28,28,0.2)',
            color: '#b91c1c', fontSize: '11.5px',
          }}>
            {error}
          </div>
        )}

        {/* ─── Barre Actions rapides ─── */}
        <div style={{
          padding: '10px 16px 0',
          borderTop: `0.5px solid ${C.borderSoft}`,
          background: '#fafaf8',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '6px',
          }}>
            <div style={{
              fontSize: '9.5px', color: C.textSubtle,
              textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600,
            }}>
              Actions rapides
            </div>
            <button
              onClick={() => setAddShortcutModal(true)}
              title="Créer un raccourci personnalisé"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '11px', color: C.textMuted, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = C.gold; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = C.textMuted; }}
            >
              <span style={{ fontSize: '13px', lineHeight: 1 }}>+</span>
              <span>Nouveau raccourci</span>
            </button>
          </div>

          <div style={{
            display: 'flex', gap: '6px', flexWrap: 'wrap',
            paddingBottom: '8px',
          }}>
            {allQuickActions.map((a) => (
              <QuickActionButton
                key={a.id}
                action={a}
                onClick={() => handleQuickAction(a)}
                onRemove={a.id.startsWith('custom-') ? () => removeCustomShortcut(a.id) : null}
              />
            ))}
          </div>
        </div>

        {/* Composer */}
        <div style={{
          padding: '4px 16px 14px',
          background: '#fafaf8',
        }}>
          <div style={{
            display: 'flex', gap: '8px',
            background: C.surface,
            border: `0.5px solid ${C.border}`,
            borderRadius: '12px',
            padding: '4px 4px 4px 12px',
            alignItems: 'flex-end',
          }}>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Demandez à Lexa l'état de vos dossiers, ou autre chose…"
              rows={1}
              disabled={sending}
              style={{
                flex: 1, border: 'none', outline: 'none', resize: 'none',
                fontSize: '13.5px', fontFamily: 'inherit', lineHeight: 1.5,
                color: C.text, background: 'transparent',
                padding: '8px 0', minHeight: '20px', maxHeight: '160px',
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!draft.trim() || sending}
              style={{
                background: C.gold,
                color: C.espresso,
                border: 'none', height: '38px', padding: '0 16px',
                borderRadius: '9px',
                cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
                opacity: draft.trim() && !sending ? 1 : 0.55,
                fontFamily: 'inherit', fontSize: '13px', fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: '6px',
                justifyContent: 'center',
                transition: 'opacity 0.15s, transform 0.1s', flexShrink: 0,
                boxShadow: '0 1px 4px rgba(217,168,71,0.4)',
                letterSpacing: '0.01em',
              }}
              title="Envoyer (Cmd/Ctrl + Enter)"
            >
              <span>Envoyer</span>
              <span style={{ fontSize: '14px', lineHeight: 1 }}>↑</span>
            </button>
          </div>
          <div style={{
            fontSize: '10px', color: C.textSubtle, marginTop: '6px', paddingLeft: '4px',
          }}>
            Cmd/Ctrl + Enter pour envoyer · Lexa peut faire des erreurs, vérifiez les infos importantes
          </div>
        </div>
      </main>

      {/* ─── Modale Pappers ─── */}
      {pappersModal && (
        <PappersModal
          docKind={pappersModal.docKind}
          label={pappersModal.label}
          onClose={() => setPappersModal(null)}
        />
      )}

      {/* ─── Modale Ajouter raccourci ─── */}
      {addShortcutModal && (
        <AddShortcutModal
          onClose={() => setAddShortcutModal(false)}
          onSave={addCustomShortcut}
        />
      )}

      {/* ─── Modale "Comment voulez-vous ajouter ce dossier ?" ─── */}
      {addDossierChoiceModal && (
        <AddDossierChoiceModal
          onClose={() => setAddDossierChoiceModal(false)}
          onChooseManual={() => {
            setAddDossierChoiceModal(false);
            navigate('/dossiers/nouveau');
          }}
          onChooseLexa={() => {
            setAddDossierChoiceModal(false);
            sendMessage("J'aimerais déposer un nouveau dossier, peux-tu me guider ?");
          }}
        />
      )}
    </div>
  );
}

// ─── Bouton d'action rapide ─────────────────────────────────────────────────
function QuickActionButton({ action, onClick, onRemove }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        onClick={onClick}
        style={{
          background: hover ? C.goldSoft : C.surface,
          border: `0.5px solid ${hover ? C.goldBorder : C.border}`,
          borderRadius: '8px',
          padding: '7px 11px',
          paddingRight: onRemove && hover ? '24px' : '11px',
          fontSize: '11.5px',
          color: C.text,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: '6px',
          lineHeight: 1.2,
          transition: 'all 0.12s',
        }}
      >
        <span style={{ fontSize: '13px' }}>{action.icon}</span>
        <span>{action.label}</span>
      </button>
      {onRemove && hover && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Supprimer ce raccourci"
          style={{
            position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: C.textSubtle, padding: '2px 4px',
            fontFamily: 'inherit', lineHeight: 1,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#b91c1c'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = C.textSubtle; }}
        >×</button>
      )}
    </div>
  );
}

// ─── Modale Pappers ────────────────────────────────────────────────────────
function PappersModal({ docKind, label, onClose }) {
  const [siren, setSiren] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const cleanSiren = siren.replace(/\s+/g, '');
  const valid = /^\d{9}$/.test(cleanSiren);

  const download = async () => {
    if (!valid || loading) return;
    setLoading(true);
    setErr('');
    try {
      const res = await api.get(`/api/dossiers/pappers/${cleanSiren}/${docKind}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docKind}-${cleanSiren}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      console.error('Erreur Pappers', e);
      const detail = e?.response?.data?.detail || 'Impossible de récupérer ce document.';
      setErr(typeof detail === 'string' ? detail : 'Erreur lors du téléchargement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ marginBottom: '14px' }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: '16px', fontWeight: 600,
          color: C.text, marginBottom: '4px',
        }}>
          {label}
        </div>
        <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
          Entrez le SIREN de la société (9 chiffres). Le document sera téléchargé en PDF.
        </div>
      </div>

      <label style={{
        display: 'block', fontSize: '10.5px', color: C.textSubtle,
        textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px',
        fontWeight: 600,
      }}>
        SIREN
      </label>
      <input
        autoFocus
        value={siren}
        onChange={(e) => setSiren(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && valid) download(); }}
        placeholder="881 414 734"
        style={{
          width: '100%', padding: '10px 12px',
          fontSize: '14px', fontFamily: 'inherit',
          border: `0.5px solid ${C.border}`,
          borderRadius: '8px', outline: 'none',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.04em', boxSizing: 'border-box',
        }}
      />
      {cleanSiren && !valid && (
        <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '6px' }}>
          Le SIREN doit comporter exactement 9 chiffres.
        </div>
      )}
      {err && (
        <div style={{
          fontSize: '11.5px', color: '#b91c1c', marginTop: '10px',
          background: 'rgba(185,28,28,0.06)', padding: '8px 10px', borderRadius: '6px',
        }}>
          {err}
        </div>
      )}

      <div style={{
        display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: `0.5px solid ${C.border}`,
            color: C.textMuted, padding: '9px 14px', borderRadius: '8px',
            fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
          }}
        >Annuler</button>
        <button
          onClick={download}
          disabled={!valid || loading}
          style={{
            background: valid && !loading ? C.gold : '#e5e1d8',
            color: valid && !loading ? C.espresso : C.textSubtle,
            border: 'none', padding: '9px 14px', borderRadius: '8px',
            fontSize: '12.5px', cursor: valid && !loading ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', fontWeight: 600,
            boxShadow: valid && !loading ? '0 1px 3px rgba(217,168,71,0.35)' : 'none',
          }}
        >
          {loading ? 'Téléchargement…' : 'Télécharger'}
        </button>
      </div>
    </ModalOverlay>
  );
}

// ─── Modale Ajouter raccourci ──────────────────────────────────────────────
function AddShortcutModal({ onClose, onSave }) {
  const [label, setLabel] = useState('');
  const [prompt, setPrompt] = useState('');

  const canSave = label.trim().length > 0 && prompt.trim().length > 0;

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ marginBottom: '14px' }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: '16px', fontWeight: 600,
          color: C.text, marginBottom: '4px',
        }}>
          Nouveau raccourci
        </div>
        <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
          Créez un bouton qui envoie automatiquement une question à Lexa.
        </div>
      </div>

      <label style={{
        display: 'block', fontSize: '10.5px', color: C.textSubtle,
        textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px',
        fontWeight: 600,
      }}>
        Libellé du bouton
      </label>
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Mes dossiers en retard"
        style={{
          width: '100%', padding: '10px 12px',
          fontSize: '13.5px', fontFamily: 'inherit',
          border: `0.5px solid ${C.border}`,
          borderRadius: '8px', outline: 'none',
          boxSizing: 'border-box', marginBottom: '14px',
        }}
      />

      <label style={{
        display: 'block', fontSize: '10.5px', color: C.textSubtle,
        textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px',
        fontWeight: 600,
      }}>
        Question envoyée à Lexa
      </label>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Liste mes dossiers dont le SLA est dépassé de plus de 5 jours"
        rows={3}
        style={{
          width: '100%', padding: '10px 12px',
          fontSize: '13.5px', fontFamily: 'inherit',
          border: `0.5px solid ${C.border}`,
          borderRadius: '8px', outline: 'none',
          boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5,
        }}
      />

      <div style={{
        display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: `0.5px solid ${C.border}`,
            color: C.textMuted, padding: '9px 14px', borderRadius: '8px',
            fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
          }}
        >Annuler</button>
        <button
          onClick={() => onSave(label, prompt)}
          disabled={!canSave}
          style={{
            background: canSave ? C.gold : '#e5e1d8',
            color: canSave ? C.espresso : C.textSubtle,
            border: 'none', padding: '9px 14px', borderRadius: '8px',
            fontSize: '12.5px', cursor: canSave ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', fontWeight: 600,
            boxShadow: canSave ? '0 1px 3px rgba(217,168,71,0.35)' : 'none',
          }}
        >
          Enregistrer
        </button>
      </div>
    </ModalOverlay>
  );
}

// ─── Modale : choix manuel vs Lexa pour créer un dossier ────────────────────
function AddDossierChoiceModal({ onClose, onChooseManual, onChooseLexa }) {
  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ marginBottom: '14px' }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: '16px', fontWeight: 600,
          color: C.text, marginBottom: '4px',
        }}>
          Comment voulez-vous déposer ce dossier ?
        </div>
        <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
          Vous avez deux options. Aucune ne demande de remplir tous les champs —
          un formaliste prendra le relais.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
        <button
          onClick={onChooseLexa}
          style={{
            background: C.gold,
            color: C.espresso,
            border: 'none',
            padding: '14px 16px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            boxShadow: '0 1px 3px rgba(217,168,71,0.35)',
            transition: 'transform 0.1s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <span style={{ fontSize: '18px', lineHeight: 1 }}>✦</span>
          <span>
            <span style={{
              display: 'block',
              fontSize: '13.5px',
              fontWeight: 600,
              marginBottom: '3px',
            }}>
              Lexa s'en occupe
            </span>
            <span style={{ fontSize: '11.5px', fontWeight: 400, lineHeight: 1.4, opacity: 0.85 }}>
              Discutez en quelques messages — Lexa pose les questions et crée le dossier.
            </span>
          </span>
        </button>

        <button
          onClick={onChooseManual}
          style={{
            background: C.surface,
            color: C.text,
            border: `0.5px solid ${C.border}`,
            padding: '14px 16px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fafaf8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = C.surface; }}
        >
          <span style={{ fontSize: '18px', lineHeight: 1 }}>📝</span>
          <span>
            <span style={{
              display: 'block',
              fontSize: '13.5px',
              fontWeight: 600,
              marginBottom: '3px',
            }}>
              Je le saisis moi-même
            </span>
            <span style={{ fontSize: '11.5px', fontWeight: 400, lineHeight: 1.4, color: C.textMuted }}>
              Formulaire classique avec dépôt de pièces et analyse IA.
            </span>
          </span>
        </button>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '14px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: C.textSubtle,
            padding: '6px 10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '12px',
          }}
        >
          Annuler
        </button>
      </div>
    </ModalOverlay>
  );
}

// ─── Overlay modale générique ───────────────────────────────────────────────
function ModalOverlay({ children, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface, borderRadius: '14px', padding: '24px',
          width: '100%', maxWidth: '460px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Bulle de message ───────────────────────────────────────────────────────
function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  if (!message.content && !message.tool_calls) return null;

  return (
    <div style={{
      display: 'flex', gap: '10px',
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start',
    }}>
      <div style={{
        width: '26px', height: '26px', borderRadius: '6px',
        background: isUser ? '#f3f3f0' : C.espresso,
        color: isUser ? C.textMuted : C.gold,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Syne', sans-serif", fontSize: '11px', fontWeight: 600,
        flexShrink: 0, marginTop: '2px',
      }}>
        {isUser ? 'V' : 'L'}
      </div>

      <div style={{ maxWidth: '78%', minWidth: 0 }}>
        {isAssistant && message.tool_calls && message.tool_calls.length > 0 && (
          <div style={{
            background: C.toolBg, border: `0.5px solid ${C.goldBorder}`,
            borderRadius: '8px', padding: '6px 10px',
            fontSize: '10.5px', color: '#a07d2c', marginBottom: '6px',
            display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 600 }}>Lexa a consulté :</span>
            {message.tool_calls.map((tc, i) => (
              <span key={i} style={{
                background: 'rgba(217,168,71,0.15)',
                padding: '1px 7px', borderRadius: '4px',
                fontFamily: "'DM Sans', monospace", fontSize: '10px',
              }}>
                {tc.name}
              </span>
            ))}
          </div>
        )}

        {message.content && (
          <div style={{
            background: isUser ? C.userBubble : C.assistantBubble,
            border: isUser ? 'none' : `0.5px solid ${C.border}`,
            borderRadius: '12px', padding: '10px 14px',
            fontSize: '13.5px', lineHeight: 1.6, color: C.text,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {message.content}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── État vide ──────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px', gap: '20px',
    }}>
      <div style={{
        width: '48px', height: '48px', borderRadius: '12px',
        background: C.espresso, color: C.gold,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Syne', sans-serif", fontSize: '22px', fontWeight: 600,
      }}>L</div>

      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: '18px', fontWeight: 600,
          marginBottom: '6px',
        }}>
          Bonjour, je suis Lexa
        </div>
        <div style={{ fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
          Posez-moi une question sur vos dossiers, demandez un statut ou un délai.
          Utilisez les <strong>actions rapides</strong> en bas pour récupérer un document officiel
          ou ajouter un dossier.
        </div>
      </div>
    </div>
  );
}

// ─── Dot animé ──────────────────────────────────────────────────────────────
function Dot({ delay }) {
  return (
    <span
      style={{
        display: 'inline-block', width: '5px', height: '5px',
        borderRadius: '50%', background: C.gold,
        animation: `lexa-dot 1.2s infinite ${delay}ms`,
      }}
    />
  );
}

if (typeof document !== 'undefined' && !document.getElementById('lexa-anim-style')) {
  const style = document.createElement('style');
  style.id = 'lexa-anim-style';
  style.textContent = `
    @keyframes lexa-dot {
      0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
      30% { opacity: 1; transform: translateY(-3px); }
    }
  `;
  document.head.appendChild(style);
}
